import { inject, Injectable } from '@angular/core';
import { CollectionRequestBuilder } from '@web/common';
import {
  AdminBrandBannerApiService,
  AdminBrandBannerCreateRequest,
  AdminBrandBannerUpdateRequest,
} from '@web/shared/module/brand';
import {
  ConfirmationService,
  TableDataPayload,
  TableLazyLoad,
} from '@web/shared/ui';
import { multimediaFromResponse } from '@web/shared/module/multimedia';
import { blank, Guid, isNotNil, once } from '@web/utils';
import { map, Observable } from 'rxjs';
import { AdminBrandBannerCollectionFilter } from '../models/admin-brand-banner-collection-filter';
import { AdminBrandBannerCollectionItem } from '../models/admin-brand-banner-collection-item';
import { AdminBrandBannerDetailsForm } from '../models/admin-brand-banner-details-form';
import { AdminBrandBannerDetails } from '../models/admin-brand-banner-details';
import { AdminBrandBannerPreviewItem } from '../models/admin-brand-banner-preview-item';

@Injectable({
  providedIn: 'root',
})
export class AdminBrandBannerDataSourceService {
  private readonly bannerApi = inject(AdminBrandBannerApiService);
  private readonly collectionRequestBuilder = inject(CollectionRequestBuilder);
  private readonly confirmation = inject(ConfirmationService);

  public fetchCollection(options: {
    brandId: Guid;
    lazyLoad: TableLazyLoad<AdminBrandBannerCollectionFilter>;
  }): Observable<TableDataPayload<AdminBrandBannerCollectionItem>> {
    const { brandId, lazyLoad } = options;

    return this.bannerApi
      .fetchCollection({
        brandId,
        request: this.collectionRequestBuilder
          .page(lazyLoad.page)
          .pageSize(lazyLoad.rows)
          .sorts((builder) =>
            builder.fromTableSort(lazyLoad.sort).buildAsArray(),
          )
          .filters((builder) =>
            builder
              .fromTableFilter(lazyLoad.filter)
              .appendText((builder) =>
                builder.caseInsensitiveLikeFor('url').build(),
              )
              .appendText((builder) => builder.equalFor('status').build())
              .appendDate((builder) => builder.rangeFor('createdAt').build())
              .appendDate((builder) =>
                builder.rangeFor('visibilityStartDate').build(),
              )
              .appendDate((builder) =>
                builder.rangeFor('visibilityEndDate').build(),
              )
              .buildAsArray(),
          )
          .build(),
      })
      .pipe(
        map(
          (response) =>
            new TableDataPayload<AdminBrandBannerCollectionItem>(
              response.items.map(
                (item) =>
                  new AdminBrandBannerCollectionItem({
                    canBeEdited: item.contextOperations.canBeEdited,
                    canBeRemoved: item.contextOperations.canBeDeleted,
                    canBeViewed: item.contextOperations.canBeViewed,
                    createdAt: item.createdAt,
                    file: multimediaFromResponse(item.file),
                    id: item.id,
                    status: item.status,
                    url: item.url,
                    visibilityEndDate: item.visibilityEndDate,
                    visibilityStartDate: item.visibilityStartDate,
                  }),
              ),
              response.pageSize,
              response.page,
              response.total,
            ),
        ),
      );
  }

  public fetchPreviewCollection(options: {
    brandId: Guid;
  }): Observable<AdminBrandBannerPreviewItem[]> {
    const { brandId } = options;

    return this.bannerApi
      .fetchCollection({
        brandId,
        request: this.collectionRequestBuilder
          .disablePagination()
          .sorts((builder) => [builder.ascendingFor('position').build()])
          .build(),
      })
      .pipe(
        map((response) =>
          response.items.map(
            (item) =>
              new AdminBrandBannerPreviewItem(
                item.id,
                multimediaFromResponse(item.file),
                item.url,
              ),
          ),
        ),
      );
  }

  public fetchDetailsById(options: {
    brandId: Guid;
    bannerId: Guid;
  }): Observable<AdminBrandBannerDetails> {
    const { bannerId, brandId } = options;

    return this.bannerApi.fetchDetailsById({ brandId, bannerId }).pipe(
      map(
        (response) =>
          new AdminBrandBannerDetails({
            canBeEdited: response.contextOperations.canBeEdited,
            canBeRemoved: response.contextOperations.canBeDeleted,
            canBeViewed: response.contextOperations.canBeViewed,
            file: multimediaFromResponse(response.file),
            id: response.id,
            redirects: response.redirects,
            smallFile: isNotNil(response.smallFile)
              ? multimediaFromResponse(response.smallFile)
              : null,
            status: response.status,
            url: response.url,
            visibilityEndDate: response.visibilityEndDate,
            visibilityStartDate: response.visibilityStartDate,
          }),
      ),
    );
  }

  public fetchDetailsFormById(options: {
    brandId: Guid;
    bannerId: Guid;
  }): Observable<AdminBrandBannerDetailsForm> {
    const { bannerId, brandId } = options;

    return this.bannerApi.fetchDetailsById({ brandId, bannerId }).pipe(
      map(
        (response) =>
          new AdminBrandBannerDetailsForm({
            fileId: response.file.id,
            smallFileId: response.smallFile?.id,
            status: response.status,
            url: response.url,
            visibilityEndDate: response.visibilityEndDate,
            visibilityStartDate: response.visibilityStartDate,
          }),
      ),
    );
  }

  public createByFormData(options: {
    brandId: Guid;
    data: AdminBrandBannerDetailsForm;
  }): Observable<void> {
    const { brandId, data } = options;

    return this.bannerApi
      .create({
        brandId,
        request: new AdminBrandBannerCreateRequest({
          fileId: data.fileId,
          smallFileId: data.smallFileId,
          status: data.status,
          url: data.url,
          visibilityEndDate: data.visibilityEndDate,
          visibilityStartDate: data.visibilityStartDate,
        }),
      })
      .pipe(blank());
  }

  public updateByFormData(options: {
    brandId: Guid;
    bannerId: Guid;
    data: AdminBrandBannerDetailsForm;
  }): Observable<void> {
    const { bannerId, brandId, data } = options;

    return this.bannerApi.update({
      bannerId,
      brandId,
      request: new AdminBrandBannerUpdateRequest({
        fileId: data.fileId,
        smallFileId: data.smallFileId,
        status: data.status,
        url: data.url,
        visibilityEndDate: data.visibilityEndDate,
        visibilityStartDate: data.visibilityStartDate,
      }),
    });
  }

  public removeById(options: {
    brandId: Guid;
    bannerId: Guid;
  }): Observable<void> {
    const { bannerId, brandId } = options;

    return new Observable<void>((subscriber) => {
      this.confirmation.show((builder) =>
        builder
          .title('Potwierdzenie')
          .message('Czy na pewno chcesz usunąć wybrany baner marki?')
          .accept(() =>
            this.bannerApi
              .remove({ brandId, bannerId })
              .pipe(once())
              .subscribe(subscriber),
          )
          .build(),
      );
    });
  }
}
